import { Product, PromotionSelectionInput, SelectedProductModifier } from '@/types';
import { hasModifiers, modifierSignature } from '@/lib/productModifiers';
import {
  StagedProductsState,
  StagedProductsStore,
} from './stagedProducts.types';
import { hasPositiveWeight } from '@/utils/weightPricing';
type SetState = (
  partial:
    | StagedProductsStore
    | Partial<StagedProductsStore>
    | ((state: StagedProductsStore) => StagedProductsStore | Partial<StagedProductsStore>),
  replace?: boolean
) => void;
type GetState = () => StagedProductsStore;

function newLineId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `line-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

const normalizeStagedNotes = (notes?: string) => notes?.trim() || '';

/** Only plain lines can merge: promo lines and weight lines (each weigh-in is its own line) never do. */
const isPlainStagedLine = (entry: {
  promotionSelections?: PromotionSelectionInput[];
  weightKg?: number;
}) => !entry.promotionSelections?.length && !hasPositiveWeight(entry.weightKg);

export const createStagedProductsActions = (set: SetState, get: GetState) => ({
  stageProduct: (
    product: Product,
    promotionSelections?: PromotionSelectionInput[],
    weightKg?: number
  ) => {
    const state = get() as StagedProductsState;
    const hasPromoSelections = Boolean(promotionSelections && promotionSelections.length > 0);
    const hasWeight = hasPositiveWeight(weightKg);

    // Weight lines never merge — each weigh-in is its own line.
    if (hasWeight) {
      set({
        stagedProducts: [
          ...state.stagedProducts,
          {
            lineId: newLineId(),
            product,
            quantity: 1,
            notes: '',
            weightKg,
            promotionSelections: hasPromoSelections ? promotionSelections : undefined,
          },
        ],
      });
      return;
    }

    if (!hasPromoSelections) {
      // Merge only into an un-noted, unmodified plain line so customized lines stay separate.
      const existingIndex = state.stagedProducts.findIndex(
        (entry) =>
          entry.product.id === product.id &&
          isPlainStagedLine(entry) &&
          normalizeStagedNotes(entry.notes) === '' &&
          !hasModifiers(entry.modifiers)
      );

      if (existingIndex !== -1) {
        const updated = [...state.stagedProducts];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + 1,
        };
        set({ stagedProducts: updated });
        return;
      }
    }

    set({
      stagedProducts: [
        ...state.stagedProducts,
        {
          lineId: newLineId(),
          product,
          quantity: 1,
          notes: '',
          promotionSelections: hasPromoSelections ? promotionSelections : undefined,
        },
      ],
    });
  },

  decreaseQuantity: (lineId: string) => {
    const state = get() as StagedProductsState;
    const updated = state.stagedProducts
      .map((entry) =>
        entry.lineId === lineId ? { ...entry, quantity: Math.max(0, entry.quantity - 1) } : entry
      )
      .filter((entry) => entry.quantity > 0);
    set({ stagedProducts: updated });
  },

  increaseQuantity: (lineId: string) => {
    const state = get() as StagedProductsState;
    const idx = state.stagedProducts.findIndex((e) => e.lineId === lineId);
    if (idx === -1) return;
    const updated = [...state.stagedProducts];
    updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + 1 };
    set({ stagedProducts: updated });
  },

  setQuantity: (lineId: string, quantity: number) => {
    const state = get() as StagedProductsState;
    if (quantity <= 0) {
      set({ stagedProducts: state.stagedProducts.filter((entry) => entry.lineId !== lineId) });
    } else {
      const updated = state.stagedProducts.map((entry) =>
        entry.lineId === lineId ? { ...entry, quantity } : entry
      );
      set({ stagedProducts: updated });
    }
  },

  setWeight: (lineId: string, weightKg: number) => {
    const state = get() as StagedProductsState;
    if (!hasPositiveWeight(weightKg)) {
      set({ stagedProducts: state.stagedProducts.filter((entry) => entry.lineId !== lineId) });
      return;
    }
    const updated = state.stagedProducts.map((entry) =>
      entry.lineId === lineId ? { ...entry, quantity: 1, weightKg } : entry
    );
    set({ stagedProducts: updated });
  },

  toggleNotes: (lineId: string) => {
    const state = get() as StagedProductsState;
    if (state.editingNotesId === lineId) {
      set({ editingNotesId: null });
    } else {
      set({ editingNotesId: lineId });
    }
  },

  updateNotes: (lineId: string, notes: string, modifiers?: SelectedProductModifier[]) => {
    const state = get() as StagedProductsState;
    const idx = state.stagedProducts.findIndex((entry) => entry.lineId === lineId);
    if (idx === -1) return;

    const line = state.stagedProducts[idx];
    const trimmed = normalizeStagedNotes(notes);
    const nextModifiers = modifiers ?? line.modifiers;
    const nextSignature = modifierSignature(nextModifiers);
    const lineModifiers = hasModifiers(nextModifiers) ? nextModifiers : undefined;

    if (
      trimmed === normalizeStagedNotes(line.notes) &&
      nextSignature === modifierSignature(line.modifiers)
    ) {
      return;
    }

    const mergeIdx = isPlainStagedLine(line)
      ? state.stagedProducts.findIndex(
          (entry, i) =>
            i !== idx &&
            entry.product.id === line.product.id &&
            isPlainStagedLine(entry) &&
            normalizeStagedNotes(entry.notes) === trimmed &&
            modifierSignature(entry.modifiers) === nextSignature
        )
      : -1;

    const isCleared = !trimmed && !lineModifiers;

    if (isCleared || line.quantity <= 1) {
      if (mergeIdx >= 0) {
        const updated = state.stagedProducts
          .map((entry, i) =>
            i === mergeIdx
              ? { ...entry, quantity: entry.quantity + line.quantity }
              : entry
          )
          .filter((entry) => entry.lineId !== lineId);
        set({ stagedProducts: updated });
        return;
      }

      const updated = [...state.stagedProducts];
      updated[idx] = { ...line, notes: trimmed, modifiers: lineModifiers };
      set({ stagedProducts: updated });
      return;
    }

    // qty > 1: peel one unit with the new notes/modifiers (merge into existing matching line if any).
    const updated = [...state.stagedProducts];
    updated[idx] = { ...line, quantity: line.quantity - 1 };

    if (mergeIdx >= 0) {
      updated[mergeIdx] = {
        ...updated[mergeIdx],
        quantity: updated[mergeIdx].quantity + 1,
      };
      set({ stagedProducts: updated });
      return;
    }

    updated.splice(idx + 1, 0, {
      lineId: newLineId(),
      product: line.product,
      quantity: 1,
      notes: trimmed,
      promotionSelections: line.promotionSelections,
      modifiers: lineModifiers,
    });
    set({ stagedProducts: updated });
  },

  updateCustomerPosition: (lineId: string, customerPosition: number | undefined) => {
    const state = get() as StagedProductsState;
    const updated = state.stagedProducts.map((entry) =>
      entry.lineId === lineId ? { ...entry, customerPosition } : entry
    );
    set({ stagedProducts: updated });
  },

  clearStagedProducts: () => {
    set({ stagedProducts: [], editingNotesId: null });
  },

  setIsCommitting: (isCommitting: boolean) => {
    set({ isCommitting });
  },

  setEditingNotesId: (id: string | null) => {
    set({ editingNotesId: id });
  },

  // Committing to a tab lives in useCommitStagedProducts (offline-capable mutation).
});
